import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useRefreshFilteredTasks,
  useRemoveTaskFromLists
} from "@/entities/tasks/hooks";
import { TaskData } from "@/entities/tasks/types";
import SuccessToast from "@/components/controls/Task/SuccessToast";

interface UseTaskActionsProps {
  task?: TaskData;
  onDelete?: () => void;
}

const TRANSLATION_KEY = "tasks.drawer.task";

export const useTaskActions = ({
  task,
  onDelete
}: UseTaskActionsProps) => {
  const { t } = useTranslation();

  const refreshTasks = useRefreshFilteredTasks();
  const removeTaskFromLists = useRemoveTaskFromLists();
  const updateTask = useUpdateTaskMutation();
  const deleteTask = useDeleteTaskMutation();

  // Local state for task completion animation
  const [localIsCompleted, setLocalIsCompleted] = useState(
    task?.isCompleted || false
  );
  const [showStrikethrough, setShowStrikethrough] = useState(
    task?.isCompleted || false
  );
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldHide, setShouldHide] = useState(false);

  const [isDeleteTaskModalOpen, setIsDeleteTaskModalOpen] = useState(false);

  useEffect(() => {
    if (task) {
      setLocalIsCompleted(task.isCompleted);
      setShowStrikethrough(task.isCompleted);
      // Fetched data now reflects the change, so clear the local hide.
      setIsAnimating(false);
      setShouldHide(false);
    }
  }, [task?.isCompleted]);

  const hasValidTask = !!task;

  const handleUpdateIsCompleted = (isCompleted: boolean) => {
    if (!hasValidTask) return;

    updateTask.mutate(
      {
        pathParams: { taskId: task.id },
        data: {
          isCompleted
        }
      },
      {
        onSuccess: () => {
          refreshTasks();

          if (isCompleted) {
            toast.success(
              <SuccessToast
                undoLabel={t("undo")}
                successTitle={t(`${TRANSLATION_KEY}.taskCompleted`)}
                handleUndo={() => {
                  // Show the row again now; fetched data may never change isCompleted.
                  setLocalIsCompleted(false);
                  setShowStrikethrough(false);
                  setIsAnimating(false);
                  setShouldHide(false);
                  handleUpdateIsCompleted(false);
                }}
                refreshTasks={refreshTasks}
              />,
              {
                duration: 5000
              }
            );
          }
        },
        onError: () => {
          setLocalIsCompleted(task.isCompleted);
          setShowStrikethrough(task.isCompleted);
          setIsAnimating(false);
          setShouldHide(false);
          toast.error(t("focus.tasks.taskUpdateFailed"));
        }
      }
    );
  };

  const handleCheckedChange = (checked: boolean) => {
    if (!hasValidTask) return;

    setLocalIsCompleted(checked);

    if (checked) {
      setShowStrikethrough(true);
      setTimeout(() => {
        setIsAnimating(true);
        setTimeout(() => {
          setShouldHide(true);
          handleUpdateIsCompleted(checked);
        }, 1000); // Wait 1 second before starting animation
      }, 1000);
    } else {
      setShowStrikethrough(false);
      handleUpdateIsCompleted(checked);
    }
  };

  const handleDeleteTask = () => {
    if (!hasValidTask) {
      setIsDeleteTaskModalOpen(false);
      return;
    }

    deleteTask.mutate(
      {
        pathParams: {
          taskId: task.id
        }
      },
      {
        onSuccess: () => {
          setIsDeleteTaskModalOpen(false);
          removeTaskFromLists(task.id);
          toast.success(t(`${TRANSLATION_KEY}.taskDeleted`), {
            duration: 5000,
            icon: null
          });
          onDelete?.();
        }
      }
    );
  };

  return {
    // Task completion states
    localIsCompleted,
    showStrikethrough,
    isAnimating,
    shouldHide,

    // Delete task modal state
    isDeleteTaskModalOpen,
    setIsDeleteTaskModalOpen,

    // Task actions
    handleCheckedChange,
    handleDeleteTask,
    handleUpdateIsCompleted,

    // Task validity flag
    hasValidTask
  };
};
